import {useMemo} from 'react'
import {History} from 'history'
import {NextRouter} from 'next/router'

import {Url} from '../context/RouterContext'
import {RouterProviderProps} from '../types'

const extractValidPathName = (maybePathName?: string | null) => {
  if (!maybePathName) return
  if (!maybePathName.startsWith('/')) return

  const url = new URL(window.location.origin + maybePathName)
  return url.pathname
}

/**
 * Wrap client-side router functions with a bail to hard navigation
 * @param router - The router instance (History or NextRouter).
 * @param isCrossingRouters - Function to determine if routers are being crossed.
 */
export const useRouterFunctionsWithBail = <Router extends History | NextRouter>(
  router: Router,
  isCrossingRouters: RouterProviderProps['isCrossingRouters']
) => {
  const [push, replace] = useMemo(
    () =>
      (['push', 'replace'] as const).map(
        (method): Router[typeof method] =>
          (newLocation: Url, ...extraArgs: unknown[]) => {
            const maybePathName =
              typeof newLocation === 'string'
                ? newLocation
                : newLocation.pathname

            // The new location might be a partial "location object", could have params
            const pathname = extractValidPathName(maybePathName)

            // Let the client router handle absolute URLs and partials
            const shouldHardNav =
              maybePathName &&
              pathname &&
              isCrossingRouters &&
              isCrossingRouters(window.location.pathname, pathname)

            if (shouldHardNav) {
              console.log('[Router] Switching routers with hard navigation')
            }

            if (shouldHardNav && method === 'push') {
              window.location.href = maybePathName
              return
            }

            if (shouldHardNav && method === 'replace') {
              window.location.replace(maybePathName)
              return
            }

            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore - incompatible router signatures (using ts-ignore because of strict/loose conflicts)
            return router[method](newLocation, ...extraArgs)
          }
      ),
    [router, isCrossingRouters]
  )
  return {push, replace}
}
